import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { LuTarget, LuChevronRight } from 'react-icons/lu';
import { buildScorecard, scopeLabel } from './scorecard';
import { share } from './objectiveFormat';

const SkeletonTile = () => (
  <div className="tw-h-[104px] tw-flex-1 tw-min-w-[210px] tw-animate-pulse tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-slate-100" />
);

const ScoreTile = ({ card, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(card.tab)}
    className="tw-group tw-flex-1 tw-min-w-[210px] tw-cursor-pointer tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-border-l-4 tw-bg-white tw-px-4 tw-py-3 tw-text-left tw-shadow-sm tw-transition-shadow hover:tw-shadow-md focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-400"
    style={{ borderLeftColor: card.accent }}
  >
    <span className="tw-mb-1 tw-flex tw-items-center tw-justify-between tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-400">
      {card.label}
      <LuChevronRight size={14} className="tw-text-slate-300 group-hover:tw-text-primary-500" />
    </span>
    <span className="tw-block tw-text-3xl tw-font-bold tw-tabular-nums tw-leading-none" style={{ color: card.tone }}>
      {card.value}
    </span>
    <span className="tw-mt-2 tw-block tw-h-1.5 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-slate-100">
      <span className="tw-block tw-h-full tw-rounded-full" style={{ width: `${share(card.bar, 1) * 100}%`, backgroundColor: card.tone }} />
    </span>
    <span className="tw-mt-1.5 tw-block tw-text-[11px] tw-text-slate-400">{card.hint}</span>
  </button>
);

ScoreTile.propTypes = {
  card:     PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
};

/**
 * The four objective numbers a manager/BOD reads first, above every tab. Each
 * tile opens the tab that explains it. A failed load never blocks the rest of
 * the dashboard — the tiles just say so.
 */
const ObjectiveScorecard = ({ data, loading, error, onSelect }) => {
  const cards = useMemo(() => buildScorecard(data), [data]);
  const scope = scopeLabel(data);

  return (
    <section className="tw-flex tw-flex-col tw-gap-2" aria-label="Objective scorecard">
      <div className="tw-flex tw-flex-wrap tw-items-baseline tw-justify-between tw-gap-2">
        <h2 className="tw-mb-0 tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600">
          <LuTarget size={15} className="tw-text-primary-600" />
          Objective scorecard
        </h2>
        <span className="tw-text-xs tw-text-slate-400">
          {error ? `Unable to load objectives — ${error}` : scope && `Counted by job (P/J number) · ${scope}`}
        </span>
      </div>

      <div className="tw-flex tw-flex-wrap tw-gap-3">
        {loading && !data
          ? cards.map((card) => <SkeletonTile key={card.key} />)
          : cards.map((card) => <ScoreTile key={card.key} card={card} onSelect={onSelect} />)}
      </div>
    </section>
  );
};

ObjectiveScorecard.propTypes = {
  data:     PropTypes.object,
  loading:  PropTypes.bool,
  error:    PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

export default ObjectiveScorecard;
